import React, { useEffect, useRef, useState } from 'react';
import './businessesList.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
	dashboardRemoveListRequest,
	dashboardRemoveAllFromSaveList,
	dashboardAddManyToSaveList,
	dashboardSetQuery
} from '@/modules/dashboard/dashboardActions';
import { getBusinesses } from '@/services/dashboardService';

import type { RootState } from '@/rootReducer';
import type {
	BusinessQuery,
	List,
	Business
} from '@/modules/dashboard/dashboardTypes';
import SaveToListModalRedesign from '@/modules/dashboard/components/SaveToListModal/SaveToListModalRedesign';
import type * as SearchTypes from '@/modules/search/searchTypes';
import Loader, { Status } from '@/components/Loader/Loader';
import { Plus, DropDown, Chevron } from '@/assets/images';
import { cn } from '@/lib/classNames';
import { sendAnalytics } from '@/services/analyticsService';
import type { User } from '@/modules/user/userTypes';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import ListModal from '../../modules/dashboard/components/ListModal/ListModal';
import ListItemRedesign from './listItemRedesign';

type BusinessesListProps = {
	headers: string[];
	businesses: Business[];
	pageName: string;
	className?: string;
	lists: List[];
	listChange: (list: string) => void;
	onCheckboxListChange: (checkboxList: { [key: string]: boolean }) => void;
	user: User;
	userSortField?: { field: string; direction: number } | null;
	setUserSortField?: React.Dispatch<
		React.SetStateAction<{ field: string; direction: number } | null>
	>;
	onSaveSelected?: () => void;
	saveToListTriggered?: boolean;
	resetSaveToListTrigger?: () => void;
	setSelectedListId?: (listId: string) => void;
	setOpenSaveToListModal: React.Dispatch<React.SetStateAction<boolean>>;
	openSaveToListModal: boolean;
	checkboxListData?: { [key: string]: boolean };
	selectedBusiness?: Business;
	onSelectBusiness: (business: Business) => void;
	largeListToSave: Business[];
	setLargeListToSave: React.Dispatch<
		React.SetStateAction<Business[] | undefined>
	>;
	onGetBusiness: (query: BusinessQuery) => void;
};

const BusinessesList: React.FC<BusinessesListProps> = ({
	user,
	pageName,
	className,
	headers,
	businesses,
	lists,
	listChange,
	onCheckboxListChange,
	userSortField,
	setUserSortField,
	onSaveSelected,
	saveToListTriggered,
	resetSaveToListTrigger,
	setSelectedListId,
	setOpenSaveToListModal,
	openSaveToListModal,
	checkboxListData,
	onSelectBusiness,
	largeListToSave,
	setLargeListToSave,
	onGetBusiness
}) => {
	const dispatch = useDispatch();

	const removeAllFromSaveList = () =>
		dispatch(dashboardRemoveAllFromSaveList());
	const removeList = (l: List) => dispatch(dashboardRemoveListRequest(l));
	const setQuery = (q: BusinessQuery) =>
		dispatch(dashboardSetQuery({ ...q, user }));
	const addManyToSaveList = (businessesList: Business[]) =>
		dispatch(dashboardAddManyToSaveList(businessesList));

	const businessSelected = useSelector((state: RootState) =>
		state.dashboard.getIn(['businesses', 'selected']).toJS()
	);
	const businessesIsLoading = useSelector((state: RootState) =>
		state.dashboard.getIn(['businesses', 'isLoading'])
	);
	const query = useSelector((state: RootState) =>
		state.dashboard.get('query').toJS()
	);
	const saveToListBusinesses = useSelector((state: RootState) =>
		state.dashboard.getIn(['lists', 'selectedBusinesses'])
	);

	const [removeListConfirm, setRemoveListConfirm] = useState<List>();
	const allowedHeaders = [
		'Business Name',
		'Name',
		'Contact Information',
		'Contact Method',
		'Lead Status',
		'Leads',
		'Email Status',
		'RE2 Score',
		'Contacted',
		'Last Contacted',
		'Locations',
		'Remove',
		'Categories',
		'Checkboxes'
	];
	const [checkboxList, setCheckboxList] = useState<{
		[key: string]: boolean;
	}>(checkboxListData || {}); // Initialize from checkboxListData
	const [saveToList, setSaveToList] = useState<Business[]>();

	const [selectedHeaderCheckboxes, setSelectedHeaderCheckboxes] = useState<{
		[key: number]: boolean;
	}>({});

	const [savedSearch, setSavedSearch] = useState<SearchTypes.Search>();
	const [editList, setEditList] = useState<List>();
	const [saveToListLoading, setSaveToListLoading] = useState<boolean>(false);
	const [
		saveListRadioInputValueSelected,
		setSaveListRadioInputValueSelected
	] = useState<string>('');
	const [saveListSliderValue, setSaveListSliderValue] = useState<number>(0);

	const filteredHeaders = headers.filter(header =>
		allowedHeaders.includes(header)
	);

	const closeSaveToList = () => {
		setOpenSaveToListModal(false);
		const outerEl = document.querySelector(`.${pageName}-header-checkbox`);
		if (outerEl) {
			outerEl.classList.remove('menu-activated'); // Close dropdown
		}
	};

	const saveToListModalRef = useRef<HTMLDivElement>(null);

	useOnClickOutside({
		ref: saveToListModalRef,
		handler: closeSaveToList,
		enabled: openSaveToListModal
	});

	// Synchronize checkboxList with checkBoxListData from parent
	useEffect(() => {
		if (JSON.stringify(checkboxList) !== JSON.stringify(checkboxListData)) {
			setCheckboxList(checkboxListData || {});
		}
	}, [checkboxListData]);

	useEffect(() => {
		if (onSaveSelected) {
			onSaveSelected(() => saveSelectedToListFunc());
		}
	}, [onSaveSelected]);

	useEffect(() => {
		if (!openSaveToListModal) return;
		// Initialize values when the modal opens
		const length = largeListToSave?.length || 0;
		setSaveListSliderValue(length);

		// Set the correct radio value based on the length of selected businesses
		if (['50', '100', '200', '3000'].includes(String(length))) {
			setSaveListRadioInputValueSelected(String(length));
		} else {
			setSaveListRadioInputValueSelected(''); // Clear if not matching specific radio options
		}
	}, [openSaveToListModal, largeListToSave?.length]); // Run when modal opens or selected businesses change

	useEffect(() => {
		if (businessSelected && businessSelected !== undefined) {
			const updatedCheckboxes = { ...checkboxListData };

			if (
				!updatedCheckboxes[
					businessSelected.businessId || businessSelected._id
				] &&
				updatedCheckboxes[
					businessSelected.businessId || businessSelected._id
				]
			) {
				updatedCheckboxes[
					businessSelected.businessId || businessSelected._id
				] = true;
			}

			setCheckboxList(updatedCheckboxes);
		}
		if (saveToListTriggered) {
			saveSelectedToListFunc(); // Trigger the function
			resetSaveToListTrigger(); // Reset the trigger
		}
	}, [saveToListTriggered, resetSaveToListTrigger]);

	const handleSort = (header: string) => {
		let normalizedHeader = header.toLowerCase();
		let setDirection = 0;
		if (normalizedHeader === 're2 score') {
			normalizedHeader = 'score';
		}

		if (userSortField?.field === normalizedHeader && setUserSortField) {
			// Already sorting by the same thing we inverse our sort
			setUserSortField({
				...userSortField,
				direction: userSortField?.direction * -1
			});
			const newSort = {
				[normalizedHeader]: String(userSortField.direction * -1)
			}; // Sort direction: 1 for ascending, -1 for descending
			const updatedQuery = {
				...query,
				sort: newSort
			};
			setQuery(updatedQuery);
			onGetBusiness(updatedQuery);
			setDirection = updatedQuery.sort[normalizedHeader];
		}
		if (userSortField?.field !== normalizedHeader && setUserSortField) {
			const newSort = { [normalizedHeader]: 1 }; // Sort direction: 1 for ascending, -1 for descending
			const updatedQuery = {
				...query,
				sort: newSort
			};
			setUserSortField({
				field: normalizedHeader,
				direction: 1
			});

			setQuery(updatedQuery);
			onGetBusiness(updatedQuery);
			setDirection = 1;
		}

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User changed sorting',
			properties: {
				page: pageName,
				sortField: normalizedHeader,
				sortOrder: setDirection === 1 ? 'ascending' : 'descending'
			}
		});
	};

	// Filter headers and keep only valid ones
	const sortableHeaders = [
		'Name',
		'RE2 Score',
		'Categories',
		'Locations',
		'Checkboxes'
	];

	// Handle checkbox click for individual businesses
	const clickItemCheckbox = (id: string) => {
		setCheckboxList(prev => {
			const isToggled = !!prev[id]; // Determine if the checkbox is currently active
			const updated = { ...prev };

			if (isToggled) {
				delete updated[id]; // Remove if active
			} else {
				updated[id] = true; // Add if inactive
			}

			onCheckboxListChange(updated); // Notify parent immediately

			setLargeListToSave(prevList => {
				const business = businesses.find(
					biz => biz?._id === id || biz?.businessId === id
				);
				if (!business) return prevList; // Ensure business exists

				const safePrevList = Array.isArray(prevList) ? prevList : [];
				if (!isToggled) {
					if (!safePrevList.some(biz => biz._id === id)) {
						return [...safePrevList, business];
					}
					return safePrevList;
				}

				return safePrevList.filter(biz => biz._id !== id);
			});

			return updated;
		});
	};

	// Select batch of businesses
	const selectQuantity = (qty: number) => {
		const updatedCheckboxes = { ...checkboxList };
		const selectedBusinesses = [...largeListToSave];
		for (let i = 0; i < qty; i++) {
			if (businesses[i]) {
				const { businessId } = businesses[i];
				updatedCheckboxes[businessId] = true; // Update the internal state
				selectedBusinesses.push(businesses[i]); // Add the business object to the list
			}
		}

		setCheckboxList(updatedCheckboxes); // Update the state with the selected checkboxes
		onCheckboxListChange(updatedCheckboxes);
		setLargeListToSave(selectedBusinesses);
		const pageIdentifier =
			businesses.length > 0
				? businesses[0].businessId || businesses[0]._id
				: null;

		if (pageIdentifier) {
			const allSelected = businesses.every(
				business => updatedCheckboxes[business.businessId]
			);

			setSelectedHeaderCheckboxes(prev => ({
				...prev,
				[pageIdentifier]: allSelected
			}));
		}

		// Close the dropdown after selecting the quantity
		const outerEl = document.querySelector(`.${pageName}-header-checkbox`);
		if (outerEl) {
			outerEl.classList.remove('menu-activated'); // Close dropdown
		}
	};

	// Clear all selected businesses
	const removeAllChecks = () => {
		setCheckboxList({});
		onCheckboxListChange({});
		setSaveListSliderValue(0);
		setSaveListRadioInputValueSelected('');
		setSelectedHeaderCheckboxes({});
		setLargeListToSave([]);

		const actives = document.getElementsByClassName('activated');
		const arr = [];
		for (let i = 0; i < actives.length; i++) {
			arr.push(actives[i]);
		}
		arr.forEach(el => {
			el.classList.remove('activated');
		});
	};

	const clickCheckbox = evt => {
		evt.stopPropagation();
		if (!evt.currentTarget.classList.contains('activated')) {
			const outerEl = document.getElementsByClassName(
				`${pageName}-header-checkbox`
			);
			if (outerEl && outerEl[0]) {
				if (outerEl[0].classList.contains('menu-activated')) {
					outerEl[0].classList.remove('menu-activated');
				} else {
					outerEl[0].classList.add('menu-activated');
				}
			}
		} else {
			setCheckboxList({});
			onCheckboxListChange({});
			evt.currentTarget.classList.remove('activated');
		}
	};

	const saveSelectedToListFunc = () => {
		const clonedObj = JSON.parse(JSON.stringify(checkboxList));
		const newList = [];
		const listToIterate =
			largeListToSave && largeListToSave.length > 0
				? largeListToSave
				: businesses;

		for (let i = 0; i < listToIterate.length; i++) {
			const item = listToIterate[i];
			const itemId = item.businessId || item._id;

			if (clonedObj[itemId] === true) {
				const newItem = { ...item, _id: itemId }; // Ensure _id is properly set
				newList.push(newItem);
			}
		}
		setSaveToList(newList);
	};

	const saveLargeList = async (listCount: number) => {
		let includesCount = 0;
		let excludesCount = 0;
		if (query.searches) {
			let includes = `[""`;
			let excludes = `[""`;
			query?.searches?.forEach(
				(point: NonNullable<BusinessQuery['searches']>[number]) => {
					if (point.type === 0) {
						includes = includes.concat(`,"${point.value}"`);
						includesCount += 1;
					} else {
						excludes = excludes.concat(`,"${point.value}"`);
						excludesCount += 1;
					}
				}
			);

			// Prepares the jsonString for parsing
			const fixJsonString = (jsonString: string) => {
				return jsonString.replace(/\\(?![\\/bfnrt"])/g, '');
			};
			excludes = excludes.concat(']');
			includes = includes.concat(']');

			includes = fixJsonString(includes);
			excludes = fixJsonString(excludes);

			const originalIncludesArray = JSON.parse(includes);
			const filteredIncludesArray = originalIncludesArray.filter(
				(item: string) => item !== ''
			);
			const filteredIncludesString = JSON.stringify(
				filteredIncludesArray
			);

			const originalExcludesArray = JSON.parse(excludes);
			const filteredExcludesArray = originalExcludesArray.filter(
				(item: string) => item !== ''
			);
			const filteredExcludesString = JSON.stringify(
				filteredExcludesArray
			);

			if (includesCount !== 0) {
				query.includes = filteredIncludesString;
			}
			if (excludesCount !== 0) {
				query.excludes = filteredExcludesString;
			}
		}

		const shouldUpdateSort =
			query.coordinates &&
			query.coordinates.length > 0 &&
			userSortField?.field === '';

		return getBusinesses({
			...query,
			limit: listCount,
			sort: shouldUpdateSort ? { score: '-1' } : query.sort,
			sortField: shouldUpdateSort ? 'score' : query.sortField,
			searchName: query.locationName
		});
	};

	const selectListAmount = async value => {
		let businessesList = businesses || []; // Fallback to an empty array if businesses is undefined

		// Clear the current checkbox list before applying new selections
		const updatedCheckboxes = {};

		if (value > 50) {
			setSaveToListLoading(true);

			// Fetch the larger list if necessary
			await saveLargeList(value).then(result => {
				businessesList = result?.data?.data?.result || []; // Safeguard against undefined results
			});

			setSaveToListLoading(false);
		}

		// Select businesses up to the specified value
		const grouping = businessesList?.length
			? businessesList.slice(0, value)
			: [];

		grouping.forEach(business => {
			const businessId = business.businessId || business._id;
			updatedCheckboxes[businessId] = true; // Add business to the checkbox list
		});

		setCheckboxList(updatedCheckboxes); // Reset and update the state with the new selection
		onCheckboxListChange(updatedCheckboxes); // Propagate the change to parent

		removeAllFromSaveList(); // Clear previous save list
		setLargeListToSave(grouping);
		addManyToSaveList(grouping); // Add the selected businesses to the save list
	};

	// Function to handle the Communication Center-specific checkbox content
	const renderCommunicationCenterCheckbox = () => (
		<div>
			<div className="checkbox-square">
				<div
					className={`list-item-checkbox-list-item-${pageName}-inner-check-left`}
				/>
				<div
					className={`list-item-checkbox-list-item-${pageName}-inner-check-right`}
				/>
			</div>
			<div className="select-number-outer">
				<div
					className="select-number-item"
					onClick={e => {
						e.stopPropagation(); // Prevent triggering the parent checkbox onClick
						selectQuantity(50);
					}}
				>
					Select next 50
				</div>
				<div
					className="select-number-item"
					onClick={e => {
						e.stopPropagation(); // Prevent triggering the parent checkbox onClick
						selectQuantity(100);
					}}
				>
					Select next 100
				</div>
				<div
					className="select-number-item"
					onClick={e => {
						e.stopPropagation(); // Prevent triggering the parent checkbox onClick
						selectQuantity(150);
					}}
				>
					Select next 150
				</div>
			</div>
		</div>
	);

	return (
		<div
			className={cn(
				'flex min-h-[50dvh] w-full flex-col overflow-auto rounded-md bg-dm-black3 md:min-h-0',
				className
			)}
		>
			<ListModal
				businesses={saveToList}
				saveBusinessToList={setSaveToList}
				removeListConfirm={removeListConfirm}
				setRemoveListConfirm={setRemoveListConfirm}
				removeList={removeList}
				savedSearch={savedSearch}
				editList={editList}
				setEditList={setEditList}
				setSavedSearch={setSavedSearch}
				listChange={listChange}
				onListSelected={setSelectedListId}
			/>

			{/* Check box selection */}
			{pageName !== 'search-businesses' && (
				<div
					className={`flex ${pageName}-business-list-save-list ${
						Object.keys(checkboxList).length > 0
							? 'save-list-open'
							: ''
					}`}
				>
					<div className="list-item-checkbox-outer list-item-checkbox-hidden-nav">
						<button
							type="button"
							className="list-item-checkbox-inner list-item-checkbox-inner-hidden-nav"
							onClick={removeAllChecks}
						>
							<div className="checkbox-white-bar" />
						</button>
					</div>
					<div className={`${pageName}-business-list-save-count`}>
						{Object.keys(checkboxList).length} selected
					</div>

					<button
						type="button"
						className={`${pageName}-save-to-list-button`}
						onClick={saveSelectedToListFunc}
					>
						<div className={`${pageName}-save-to-list-text`}>
							Save to Prospecting List
						</div>
						<Plus className="save-to-list-plus-icon" />
					</button>
				</div>
			)}
			{/* Header */}
			<div className={`${pageName}-list-header sticky top-0 z-10`}>
				<div className={`${pageName}-list-header-content`}>
					{filteredHeaders.map((header, index) => {
						const normalizedHeader =
							header.toLowerCase() === 're2 score'
								? 'score'
								: header.toLowerCase();
						return (
							<div
								key={index}
								data-checkboxactive={
									businesses.length > 0 &&
									businesses.every(
										biz =>
											checkboxList[biz.businessId] ||
											checkboxList[biz._id]
									) &&
									index === 0
										? 'true'
										: 'false'
								}
								data-selected={
									selectedHeaderCheckboxes[
										businesses[0]?.businessId ||
											businesses[0]?._id
									] && index === 0
										? 'true'
										: 'false'
								}
								className={cn(
									`${pageName}-list-header-column-${index}`,
									userSortField?.field === normalizedHeader &&
										'active-sort'
								)}
							>
								{header === 'Checkboxes' ? (
									<button
										type="button"
										onClick={e => {
											// Check if the parent element has the "activated" class
											const isActive =
												businesses.length > 0 &&
												businesses.every(
													biz =>
														checkboxList[
															biz.businessId
														] ||
														checkboxList[biz._id]
												);

											setOpenSaveToListModal(true);

											if (isActive) {
												// If the checkbox is active, call the removeAllChecks function
												clickCheckbox(e);
											} else {
												// Otherwise, proceed with the normal checkbox click behavior
												clickCheckbox(e);
											}
										}}
										className={`${pageName}-header-checkbox ${
											pageName === 'search-businesses'
												? `${pageName}-header-checkbox-container`
												: ''
										}`}
									>
										{/* Conditionally render the Communication Center content */}
										{pageName === 'communication-center' &&
											renderCommunicationCenterCheckbox()}
										{openSaveToListModal &&
											pageName ===
												'search-businesses' && (
												<div ref={saveToListModalRef}>
													<SaveToListModalRedesign
														largeListToSave={
															largeListToSave
														}
														closeSaveToList={
															closeSaveToList
														}
														saveListRadioInputValueSelected={
															saveListRadioInputValueSelected
														}
														setSaveListRadioInputValueSelected={value => {
															setSaveListRadioInputValueSelected(
																value
															);
														}}
														saveListSliderValue={
															saveListSliderValue ||
															0
														}
														setSaveListSliderValue={value => {
															setSaveListSliderValue(
																value
															);
														}}
														selectListAmount={
															selectListAmount
														}
														saveToListLoading={
															saveToListLoading
														}
														saveBusinessesToList={
															setSaveToList
														}
													/>
												</div>
											)}

										{/* Only render DropdownArrowDown for the search-businesses page */}
										{pageName === 'search-businesses' && (
											<>
												<div className="checkbox-square">
													<div
														className={`list-item-checkbox-list-item-${pageName}-inner-check-left`}
													/>
													<div
														className={`list-item-checkbox-list-item-${pageName}-inner-check-right`}
													/>
												</div>
												<Chevron className="checkbox-dropdown" />
											</>
										)}
									</button>
								) : (
									<div
										className="header-with-dropdown"
										onClick={() => {
											if (
												pageName ===
													'search-businesses' &&
												sortableHeaders.includes(header)
											) {
												handleSort(header); // Sort on click for sortable columns
											}
										}}
									>
										{header}
										{/* Conditionally render the active sort arrow only for the sorted column */}
										{pageName === 'search-businesses' &&
											sortableHeaders.includes(
												header
											) && (
												<DropDown
													className={`dropdown ${
														userSortField?.field ===
														normalizedHeader
															? userSortField?.direction !==
																1
																? 'arrow-up'
																: 'arrow-down'
															: ''
													}`}
												/>
											)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			</div>

			{/* List Items */}
			<div className={`${pageName}-items-list relative`}>
				{businessesIsLoading ? (
					// Render the loader while businesses are loading
					<div className="align-center flex h-full w-full items-center justify-center fade-in">
						<Loader status={Status.LOADING} />
					</div>
				) : (
					<section className="absolute bottom-0 left-0 right-0 top-0 w-fit min-w-full">
						{businesses.map((business, index) => {
							const isSelected = Boolean(
								(business.businessId &&
									business.businessId ===
										businessSelected.businessId) ||
									(business._id &&
										business._id ===
											businessSelected._id) ||
									(business.id &&
										business.id === businessSelected.id)
							);

							return (
								<ListItemRedesign
									businessData={business}
									key={
										business.businessId ||
										business._id ||
										index
									}
									newMessage={business.emailIsRead === false}
									selected={isSelected}
									initialHeaders={filteredHeaders}
									toggleBusinessPreview={() =>
										onSelectBusiness(business)
									}
									pageName={pageName}
									checkboxList={checkboxList}
									clickItemCheckbox={clickItemCheckbox}
									isClicked={
										businessSelected.businessId ===
										(business.businessId || business._id)
									} // Pass down clicked status
									data-business-id={
										business.businessId || business._id
									}
								/>
							);
						})}
					</section>
				)}
			</div>
		</div>
	);
};

export default BusinessesList;
